import { useApp } from 'contexts/app';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Button, Container, Divider, Icon, Paper, Stack, styled, Text, TextInput } from 'shell/components';
import { FEEDBACK_LIMIT } from 'utils/constants';
import { getUserPreferredFullName, last4 } from 'utils/helper';

const Mobile = ({ filters, setFilters, getFilterCount, onClickBack, onClickView, onClickViewAll }) => {
    const { t } = useTranslation();
    const { studentAccountUsers, currentAssignment, feedback: allFeedback } = useApp();

    const feedback = allFeedback.filter(({ owner_account_id }) => {
        return studentAccountUsers.some((student) => student.owner_account_id === owner_account_id);
    });

    return (
        <Container device={'mobile'}>
            <CustomStack spacing={5}>
                <Stack alignItems={'start'} spacing={4}>
                    <Button startIcon={<Icon kind={'arrowLeft'} />} variant={'borderless'} onClick={onClickBack}>
                        {t('controls.back', 'Back')}
                    </Button>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {currentAssignment.title}
                    </Text>
                    <CustomStack alignItems={'center'} direction={'row'} spacing={4}>
                        <CustomButton
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            variant={'secondary'}
                            onClick={() => setFilters((current) => ({ ...current, open: true }))}
                        >
                            ({getFilterCount(filters)})
                        </CustomButton>
                        <TextInput
                            endIcon={<Icon kind={'search'} />}
                            placeholder={t('controls.search', 'Search')}
                            value={filters.search}
                            onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                        />
                    </CustomStack>
                </Stack>
                <Button disabled={!feedback.length} onClick={onClickViewAll}>
                    {t('controls.view-all', 'View All')}
                </Button>
                <Stack spacing={4}>
                    {studentAccountUsers
                        .filter((accountUser) => {
                            return getUserPreferredFullName(accountUser)
                                .toLowerCase()
                                .includes(filters.search.toLowerCase());
                        })
                        .sort((a, b) => {
                            const aName = getUserPreferredFullName(a).toLowerCase();
                            const bName = getUserPreferredFullName(b).toLowerCase();
                            if (filters.sort === 'namea-z') {
                                if (aName < bName) return -1;
                                if (aName > bName) return 1;
                            }
                            if (filters.sort === 'namez-a') {
                                if (aName < bName) return 1;
                                if (aName > bName) return -1;
                            }
                            return 0;
                        })
                        .map((accountUser) => {
                            const feedbackItems = feedback.filter(
                                ({ owner_account_id }) => owner_account_id === accountUser.owner_account_id
                            );
                            return (
                                <Paper key={accountUser.owner_account_id} variant={'outlined'}>
                                    <Stack divider={<Divider />} spacing={2}>
                                        <Stack
                                            alignItems={'flex-start'}
                                            direction={'row'}
                                            justifyContent={'space-between'}
                                        >
                                            <Text>{last4(accountUser.user_id)}</Text>
                                            <Text variant={'sh2'} weight={'semibold'}>
                                                {getUserPreferredFullName(accountUser)}
                                            </Text>
                                            <Button
                                                disabled={!feedbackItems.length}
                                                variant={'borderless'}
                                                onClick={() =>
                                                    onClickView(
                                                        feedbackItems.sort((a, b) => b.created_on - a.created_on)[0]
                                                    )
                                                }
                                            >
                                                {t('controls.view', 'View')}
                                            </Button>
                                        </Stack>
                                        <Stack spacing={2}>
                                            <Text color={'light'}>
                                                {t('table.last-generation', 'Last Generation')}:{' '}
                                                {feedbackItems.length
                                                    ? moment(
                                                          Math.max(...feedbackItems.map(({ created_on }) => created_on))
                                                      ).format('MMM D [@] hh:mma')
                                                    : '-'}
                                            </Text>
                                            <Text color={'light'}>
                                                {t('table.feedback-attempts-used', 'Feedback Attempts Used')}:{' '}
                                                {feedbackItems.length}/{FEEDBACK_LIMIT}
                                            </Text>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            );
                        })}
                </Stack>
            </CustomStack>
        </Container>
    );
};

const CustomStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomButton = styled(Button)(() => {
    return {
        flexShrink: 0,
    };
});

export default Mobile;
